import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import ResultsModal from './ResultsModal.js';
import LoadingScreen from './LoadingScreen.js';
import ThemeDisplay from './ThemeDisplay.js';
import './ReelConnectionsGame.css';

const ReelConnectionsGame = ({ user, userId, isAuthenticated, isDarkMode, stats, onBack, selectedDate, fetchStats, fetchHistoricalQuizzes }) => {
    const [quiz, setQuiz] = useState(null);
    const [grid, setGrid] = useState([]); // Array of 16 emojis
    const [selected, setSelected] = useState([]); // Array of indices
    const [foundGroups, setFoundGroups] = useState([]); // Array of found groups { movie_title, emojis, difficulty }
    const [mistakes, setMistakes] = useState(0);
    const [maxMistakes] = useState(4);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [shake, setShake] = useState(false);
    const [attempts, setAttempts] = useState([]); // Array of color arrays like [['yellow', 'yellow', 'yellow', 'blue'], ...]

    // For results modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [populationStats, setPopulationStats] = useState(null);
    const [finalResults, setFinalResults] = useState('');
    const [solvedCount, setSolvedCount] = useState(0);

    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || '/api';

    // Stable formatting function (no state dependencies)
    const formatResultsString = useCallback((attemptsToUse, quizContext, groupsFallback) => {
        const theme = quizContext?.theme || "iconn🧩ct";
        const dateStr = (quizContext?.date || DateTime.utc().toISODate()) + "\n";
        const title = `iconn🧩ct\n"${theme}"\n`;

        const colorMap = {
            'yellow': '🟨',
            'green': '🟩',
            'blue': '🟦',
            'purple': '🟪'
        };

        let blocks = "";
        if (attemptsToUse && attemptsToUse.length > 0) {
            blocks = attemptsToUse.map(row =>
                row.map(color => colorMap[color] || '⬜️').join('')
            ).join('\n');
        } else if (groupsFallback && groupsFallback.length > 0) {
            blocks = groupsFallback.map(g => {
                const emoji = colorMap[g.difficulty] || '⬜️';
                return emoji.repeat(4);
            }).join('\n');

            if (groupsFallback.length < 4) {
                const empty = '⬜️'.repeat(4);
                const needed = 4 - groupsFallback.length;
                for (let i = 0; i < needed; i++) {
                    blocks += `\n${empty}`;
                }
            }
        }

        const dateValue = quizContext?.date || DateTime.utc().toISODate();
        return `${title}${dateStr}\n${blocks}\n\nhttps://mojiequiz.com?game=iconnect&date=${dateValue}`;
    }, []);

    const fetchQuiz = useCallback(async () => {
        const resetGame = () => {
            setFoundGroups([]);
            setMistakes(0);
            setSelected([]);
            setAttempts([]);
            setIsModalOpen(false);
        };

        setLoading(true);
        resetGame();

        try {
            const quizResponse = await axios.get(`${apiEndpoint}/quiz?game_slug=reel-connections&date=${selectedDate}`);
            const quizData = quizResponse.data;

            let finalFound = [];
            let finalMistakes = 0;
            let finalAttempts = [];
            let finalModalOpen = false;
            let finalSolved = 0;
            let finalResultsText = '';

            if (userId) {
                try {
                    const progressResponse = await axios.get(`${apiEndpoint}/progress?userId=${userId}&quizId=${selectedDate}&gameId=reel-connections`);
                    if (progressResponse.data) {
                        const { progress, quiz_status } = progressResponse.data;
                        if (progress) {
                            finalFound = progress.foundGroups || [];
                            finalMistakes = progress.mistakes || 0;
                            finalAttempts = progress.attempts || [];
                        }

                        if (quiz_status === 'completed') {
                            finalModalOpen = true;
                            finalSolved = finalFound.length;
                            finalResultsText = formatResultsString(finalAttempts, quizData, finalFound);

                            if (finalFound.length < 4 && quizData.movies) {
                                const remaining = quizData.movies.filter(m =>
                                    !finalFound.some(g => g.title.toLowerCase() === m.title.toLowerCase())
                                );
                                finalFound = [...finalFound, ...remaining.map(m => {
                                    // Find available indices for these emojis
                                    const mIndices = [];
                                    m.connections_emojis.forEach(e => {
                                        const idx = quizData.grid.findIndex((tile, i) =>
                                            tile === e && !finalFound.some(g => (g.indices || []).includes(i)) && !mIndices.includes(i)
                                        );
                                        if (idx !== -1) mIndices.push(idx);
                                    });

                                    return {
                                        title: m.title,
                                        emojis: m.connections_emojis,
                                        difficulty: m.difficulty,
                                        indices: mIndices
                                    };
                                })];
                            }
                        }
                    }
                } catch (pe) {
                    console.error('Error fetching progress:', pe);
                }
            }

            setQuiz(quizData);
            setGrid(quizData.grid);
            setFoundGroups(finalFound);
            setMistakes(finalMistakes);
            setAttempts(finalAttempts);
            setSolvedCount(finalSolved);
            setFinalResults(finalResultsText);
            setIsModalOpen(finalModalOpen);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching quiz:', error);
            setLoading(false);
        }
    }, [apiEndpoint, selectedDate, userId, formatResultsString]);

    useEffect(() => {
        fetchQuiz();
    }, [fetchQuiz]);

    const showToast = (message) => {
        setToast(message);
        setTimeout(() => setToast(null), 2000);
    };

    const handleTileClick = (index) => {
        if (mistakes >= maxMistakes || foundGroups.length === 4) return;
        // Check if index is already part of a found group
        if (foundGroups.some(g => (g.indices || []).includes(index))) return;

        setSelected(prev => {
            if (prev.includes(index)) {
                return prev.filter(i => i !== index);
            }
            if (prev.length < 4) {
                return [...prev, index];
            }
            return prev;
        });
    };

    const updateProgress = async (currentFound, currentMistakes, currentAttempts, quizStatus = 'in_progress') => {
        if (!quiz) return;
        try {
            await axios.post(`${apiEndpoint}/progress`, {
                userId,
                progress: { foundGroups: currentFound, mistakes: currentMistakes, attempts: currentAttempts },
                quizId: quiz.id,
                date: quiz.date || DateTime.utc().toISODate(),
                quizStatus,
                gameId: quiz.game_id
            });
        } catch (error) {
            console.error('Progress update error:', error.message);
        }
    };

    const handleShareResults = async (finalFound, finalMistakes, finalAttempts) => {
        if (!quiz) return;
        const correctCount = finalFound.length;
        try {
            const response = await axios.post(`${apiEndpoint}/results`, {
                userId,
                gameId: quiz.game_id,
                quizId: quiz.id,
                correctCount: correctCount,
                hintsUsed: 0,
                progress: { foundGroups: finalFound, mistakes: finalMistakes, attempts: finalAttempts },
                date: quiz.date || DateTime.utc().toISODate()
            });
            setPopulationStats(response.data.stats);
            if (fetchStats) fetchStats();
            if (fetchHistoricalQuizzes) fetchHistoricalQuizzes();
        } catch (error) {
            console.error('Error submitting results:', error.message);
        }
    };

    const handleSubmit = async () => {
        if (selected.length !== 4) return;

        const selectedEmojis = selected.map(i => grid[i]);
        const matchedMovie = quiz.movies.find(movie => {
            const movieEmojis = movie.connections_emojis;
            return movieEmojis.every(e => selectedEmojis.includes(e));
        });

        // Resolve colors: If correct, show the movie color. If wrong, show the physical tile's "true home"
        const currentAttemptColors = matchedMovie
            ? Array(4).fill(matchedMovie.difficulty)
            : selected.map(index => quiz.grid_difficulties ? quiz.grid_difficulties[index] : 'unknown');

        const updatedAttempts = [...attempts, currentAttemptColors];
        setAttempts(updatedAttempts);

        if (matchedMovie) {
            const newGroup = {
                title: matchedMovie.title,
                emojis: matchedMovie.connections_emojis,
                difficulty: matchedMovie.difficulty,
                indices: [...selected] // Track indices found
            };

            const updatedFound = [...foundGroups, newGroup];
            setFoundGroups(updatedFound);
            setSelected([]);

            if (updatedFound.length === 4) {
                handleWin(updatedFound, mistakes, updatedAttempts);
            } else {
                updateProgress(updatedFound, mistakes, updatedAttempts);
            }
        } else {
            setShake(true);
            setTimeout(() => setShake(false), 500);

            const movieOverlap = quiz.movies.map(movie => {
                const overlap = movie.connections_emojis.filter(e => selectedEmojis.includes(e)).length;
                return overlap;
            });

            if (movieOverlap.some(count => count === 3)) {
                showToast("One Away!");
            }

            const newMistakes = mistakes + 1;
            setMistakes(newMistakes);
            if (newMistakes >= maxMistakes) {
                handleLoss(foundGroups, newMistakes, updatedAttempts);
            } else {
                updateProgress(foundGroups, newMistakes, updatedAttempts);
            }
        }
    };

    const handleWin = (finalFound, finalMistakes, finalAttempts) => {
        showToast("Perfect!");
        setSolvedCount(finalFound.length);
        const shareStr = formatResultsString(finalAttempts, quiz, finalFound);
        setFinalResults(shareStr);
        handleShareResults(finalFound, finalMistakes, finalAttempts);
        updateProgress(finalFound, finalMistakes, finalAttempts, 'completed');
        setTimeout(() => setIsModalOpen(true), 1500);
    };

    const handleLoss = (finalFound, finalMistakes, finalAttempts) => {
        showToast("Nice Try!");
        setSolvedCount(finalFound.length);
        const shareStr = formatResultsString(finalAttempts, quiz, finalFound);
        setFinalResults(shareStr);

        const remainingMovies = quiz.movies.filter(movie =>
            !finalFound.some(g => g.title.toLowerCase() === movie.title.toLowerCase())
        );

        const allGroups = [...finalFound, ...remainingMovies.map(m => {
            const mIndices = [];
            m.connections_emojis.forEach(e => {
                const idx = grid.findIndex((tile, i) =>
                    tile === e && !finalFound.some(g => (g.indices || []).includes(i)) && !mIndices.includes(i)
                );
                if (idx !== -1) mIndices.push(idx);
            });

            return {
                title: m.title,
                emojis: m.connections_emojis,
                difficulty: m.difficulty,
                indices: mIndices
            };
        })];

        setFoundGroups(allGroups);
        handleShareResults(finalFound, finalMistakes, finalAttempts);
        updateProgress(finalFound, finalMistakes, finalAttempts, 'completed');
        setTimeout(() => setIsModalOpen(true), 2500);
    };

    const copyResultsToClipboard = useCallback(() => {
        const shareStr = finalResults || formatResultsString(attempts, quiz, foundGroups);
        navigator.clipboard.writeText(shareStr);
    }, [finalResults, attempts, quiz, foundGroups, formatResultsString]);

    const closeModal = useCallback(() => setIsModalOpen(false), []);

    if (loading || !quiz || !grid.length) return <LoadingScreen loading={true} />;

    return (
        <div className="connections-container connections-game">
            <ThemeDisplay
                theme={quiz?.theme}
                isDarkMode={isDarkMode}
                date={selectedDate}
            />

            <div className={`connections-grid ${shake ? 'shake' : ''}`}>
                {foundGroups.map((group) => (
                    <div key={group.title} className={`group-banner group-${group.difficulty}`}>
                        <h3>{group.title.toUpperCase()}</h3>
                        <p>{group.emojis.join(' ')}</p>
                    </div>
                ))}

                {foundGroups.length < 4 && grid.map((emoji, index) => {
                    const isFound = foundGroups.some(g => {
                        if (g.indices && g.indices.length > 0) {
                            return g.indices.includes(index);
                        }
                        return g.emojis.includes(emoji);
                    });
                    if (isFound) return null;

                    return (
                        <div
                            key={index}
                            className={`emoji-tile ${selected.includes(index) ? 'selected' : ''}`}
                            onClick={() => handleTileClick(index)}
                        >
                            {emoji}
                        </div>
                    );
                })}
            </div>

            {foundGroups.length < 4 && (
                <div className="mistakes-tracker">
                    Mistakes remaining:
                    {[...Array(maxMistakes)].map((_, i) => (
                        <div key={i} className={`mistake-dot ${i < (maxMistakes - mistakes) ? 'active' : ''}`} />
                    ))}
                </div>
            )}

            <div className={`connections-controls ${foundGroups.length === 4 ? 'game-over' : ''}`}>
                {foundGroups.length === 4 ? (
                    <button className="mojie-btn primary" onClick={() => setIsModalOpen(true)}>View Results →</button>
                ) : (
                    <>
                        <button className="mojie-btn" onClick={() => setGrid([...grid].sort(() => Math.random() - 0.5))} disabled={mistakes >= maxMistakes || foundGroups.length === 4}>Shuffle</button>
                        <button className="mojie-btn" onClick={() => setSelected([])} disabled={selected.length === 0 || mistakes >= maxMistakes || foundGroups.length === 4}>Deselect All</button>
                        <button className="mojie-btn primary" onClick={handleSubmit} disabled={selected.length !== 4 || mistakes >= maxMistakes || foundGroups.length === 4}>Submit</button>
                    </>
                )}
            </div>

            {toast && <div className="toast">{toast}</div>}

            <ResultsModal
                isOpen={isModalOpen}
                onClose={closeModal}
                results={finalResults || formatResultsString(attempts, quiz, foundGroups)}
                onCopy={copyResultsToClipboard}
                theme={quiz?.theme}
                isDarkMode={isDarkMode}
                stats={stats}
                gameType="connections"
                answers={solvedCount || foundGroups.length}
                mistakes={mistakes}
                populationStats={populationStats}
            />
        </div>
    );
};

export default ReelConnectionsGame;
